import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { PostHogProvider } from '@posthog/react';
import './index.css';
import App from './App';
import { isSpeakUpHost } from './lib/speakupHost';

// ---------------------------------------------------------------------------
// SPEAK UP RUNS NO ANALYTICS AT ALL. NOT MASKED -- ABSENT.
//
// Found Sep 4, 2026, minutes after speakup.team went live, by reading what the
// page actually loaded rather than by reading this file. Every one of these
// was live on the anonymous reporting page:
//
//   posthog-recorder.js          PostHog's SESSION RECORDER
//   dead-clicks-autocapture.js   click autocapture
//   surveys.js, web-vitals.js    behavioural instrumentation
//   a persistent visitor id      `ph_phc_..._posthog` in that origin's
//                                localStorage, measured, not inferred
//   Sentry                       enabled in production, and its beforeSend
//                                returns the event unchanged -- no scrubbing
//
// Meanwhile the page says, in two places a reporter reads before deciding
// whether to speak: "Nothing on this page records who you are" and "We do not
// record your name, your email, or anything that identifies you."
//
// THOSE SENTENCES WERE NOT TRUE. A persistent per-visitor id plus behavioural
// capture is exactly what they rule out. This is the same defect class as
// #474 and worse: #474 was a token reaching OUR server, this was a third
// party recording behaviour on a whistleblowing page.
//
// WHY EXCLUDE RATHER THAN MASK, and it is the whole decision: "we masked most
// fields" is a promise about configuration, and configuration drifts. A
// library that is never loaded cannot record anything, cannot regress when
// somebody enables a feature in a vendor dashboard, and needs no audit. The
// page's promise becomes a property of the build rather than a setting.
//
// KNOWN LIMIT, stated rather than papered over: this is decided ONCE at page
// load. The origin check is airtight -- on speakup.team nothing initialises,
// ever. The PATH check covers somebody landing directly on getstede.com's
// /speak-up. It does NOT cover a signed-in employee already inside Stede who
// navigates client-side to /speak-up, because analytics booted before they
// moved. That person is already identified in Stede and is not the reporter
// this protects; the reporter arrives from a QR code, cold, on speakup.team.
// Say so out loud rather than claiming the guard is total.
const ON_SPEAK_UP =
  isSpeakUpHost() ||
  window.location.pathname.toLowerCase().startsWith('/speak-up');

if (!ON_SPEAK_UP) {
  Sentry.init({
    dsn: 'https://00e538145bd2cdf19211fe854889156a@o4511446615326720.ingest.us.sentry.io/4511446631579648',
    environment: process.env.NODE_ENV,
    enabled: process.env.NODE_ENV === 'production',
    beforeSend(event) {
      return event;
    },
  });
}

const posthogOptions = {
  api_host: 'https://us.i.posthog.com',
  defaults: '2026-01-30',
};

const root = ReactDOM.createRoot(document.getElementById('root'));

// The app itself, identical either way. Only the wrapper differs, so there is
// no second copy of the tree to drift out of step with the first.
const tree = (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

root.render(
  <React.StrictMode>
    {ON_SPEAK_UP ? (
      tree
    ) : (
      <PostHogProvider
        apiKey="phc_q9HErjev3UiYn54C9xKFeU4EaCgFRR6FqKMLruEusZKF"
        options={posthogOptions}
      >
        {tree}
      </PostHogProvider>
    )}
  </React.StrictMode>
);